import React, { useState, useEffect } from 'react';
import { Plus, Search, CheckCircle, Circle, Clock, AlertCircle, Edit2, Trash2, X, Flag, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import axios from 'axios';
import { formatDateDisplay, normalizeDateForStorage } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeTaskDates = (task) => ({
  ...task,
  dueDate: normalizeDateForStorage(task?.dueDate),
  completedAt: normalizeDateForStorage(task?.completedAt)
});

const Tasks = () => {
  const { currentUser, users, isTechnician } = useAuth();
  const { addNotification } = useNotification();
  const activeUsers = users.filter((user) => user.active !== false);
  const currentUserId = currentUser?.id || '';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmar',
    confirmClassName: 'bg-red-600 hover:bg-red-700',
    action: null
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    assignedToName: '',
    priority: 'medium',
    dueDate: '',
    status: 'pending'
  });

  const assignableUsers = activeUsers.filter(u => u.role === 'admin' || u.role === 'office');
  const pageTitle = isTechnician ? 'Mis Solicitudes' : 'Gestión de Solicitudes';
  const pageDescription = isTechnician
    ? 'Crea y revisa las solicitudes enviadas a administración'
    : 'Organiza y da seguimiento a las solicitudes del equipo';
  const emptyTitle = isTechnician ? 'No tienes solicitudes' : 'No hay solicitudes';
  const emptyDescription = isTechnician
    ? 'Crea una nueva solicitud para enviarla a administración'
    : 'Crea una nueva solicitud para comenzar';
  const statusLabels = {
    'pending': 'Pendiente',
    'in-progress': 'En progreso',
    'completed': 'Completada'
  };

  // Fetch tasks
  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tasks`);
      setTasks((res.data || []).map(normalizeTaskDates));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Refresh tasks
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchTasks();
      addNotification('Solicitudes actualizadas', 'La información se ha recargado correctamente', 'success');
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const visibleTasks = isTechnician
    ? tasks.filter(task => task.assignedBy === currentUserId || task.assignedTo === currentUserId)
    : tasks;

  // Filter tasks
  const filteredTasks = visibleTasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus ? task.status === filterStatus : true;
    const matchesPriority = filterPriority ? task.priority === filterPriority : true;
    const matchesAssignedTo = !isTechnician && filterAssignedTo ? task.assignedTo === filterAssignedTo : true;
    return matchesSearch && matchesStatus && matchesPriority && matchesAssignedTo;
  });

  // Pagination
  const {
    currentPage,
    itemsPerPage,
    paginatedData: paginatedTasks,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange
  } = usePagination(filteredTasks, 25);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignedTo: '',
      assignedToName: '',
      priority: 'medium',
      dueDate: '',
      status: 'pending'
    });
    setIsEditMode(false);
    setSelectedTask(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      addNotification('Campo requerido', 'El título es requerido', 'warning');
      return;
    }

    const assignedUser = !isTechnician ? assignableUsers.find(u => u.id === formData.assignedTo) : null;
    const taskData = {
      ...formData,
      dueDate: normalizeDateForStorage(formData.dueDate),
      status: isTechnician && !isEditMode ? 'pending' : formData.status,
      assignedTo: isTechnician ? '' : formData.assignedTo,
      assignedToName: assignedUser ? assignedUser.name : '',
      assignedBy: isEditMode && selectedTask ? selectedTask.assignedBy || currentUserId : currentUserId,
      assignedByName: isEditMode && selectedTask ? selectedTask.assignedByName || currentUser?.name || 'Admin' : currentUser?.name || 'Admin'
    };

    try {
      if (isEditMode && selectedTask) {
        await axios.put(`${API_URL}/api/tasks/${selectedTask.id}`, taskData);
        addNotification('Solicitud actualizada', 'La solicitud se actualizó correctamente', 'success');
      } else {
        await axios.post(`${API_URL}/api/tasks`, taskData);
        addNotification('Solicitud creada', 'La nueva solicitud se creó exitosamente', 'success');
      }

      fetchTasks();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving task:', error);
      addNotification('Error', 'Error al guardar la solicitud', 'error');
    }
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assignedTo: task.assignedTo || '',
      assignedToName: task.assignedToName || '',
      priority: task.priority,
      dueDate: task.dueDate || '',
      status: task.status
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${task.id}`, {
        ...task,
        status: newStatus,
        completedAt: newStatus === 'completed' ? new Date().toISOString() : task.completedAt
      });
      fetchTasks();
      addNotification('Estado actualizado', `Solicitud marcada como ${statusLabels[newStatus] || newStatus}`, 'success');
    } catch (error) {
      console.error('Error updating task status:', error);
      addNotification('Error', 'Error al actualizar el estado', 'error');
    }
  };

  const closeConfirmDialog = () => {
    setConfirmState((prev) => ({
      ...prev,
      open: false,
      action: null
    }));
  };

  const runConfirmAction = async () => {
    if (!confirmState.action) return;

    try {
      await confirmState.action();
    } finally {
      closeConfirmDialog();
    }
  };

  const handleDelete = async (taskId) => {
    setConfirmState({
      open: true,
      title: 'Eliminar solicitud',
      message: 'Esta solicitud se eliminará definitivamente.',
      confirmLabel: 'Eliminar',
      confirmClassName: 'bg-red-600 hover:bg-red-700',
      action: async () => {
        try {
          await axios.delete(`${API_URL}/api/tasks/${taskId}`);
          fetchTasks();
          addNotification('Solicitud eliminada', 'La solicitud se eliminó correctamente', 'info');
        } catch (error) {
          console.error('Error deleting task:', error);
          addNotification('Error', 'Error al eliminar la solicitud', 'error');
        }
      }
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Circle size={16} className="text-amber-500" />;
      case 'in-progress': return <Clock size={16} className="text-blue-500" />;
      case 'completed': return <CheckCircle size={16} className="text-emerald-500" />;
      default: return <Circle size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': 'bg-amber-100 text-amber-700',
      'in-progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-emerald-100 text-emerald-700'
    };
    return (
      <span className={`inline-flex items-center gap-1 ${badges[status]} text-xs font-semibold px-3 py-1 rounded-full`}>
        {getStatusIcon(status)}
        {statusLabels[status]}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': 'bg-slate-100 text-slate-600',
      'medium': 'bg-orange-100 text-orange-600',
      'high': 'bg-red-100 text-red-600'
    };
    const labels = {
      'low': 'Baja',
      'medium': 'Media',
      'high': 'Alta'
    };
    return (
      <span className={`inline-flex items-center gap-1 ${badges[priority]} text-xs font-medium px-2 py-1 rounded`}>
        <Flag size={12} />
        {labels[priority]}
      </span>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">{pageTitle}</h1>
          <p className="text-slate-500 text-sm mt-1">{pageDescription}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2.5 rounded-xl hover:bg-slate-700 transition-all shadow-sm font-medium disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? 'Actualizando...' : 'Refrescar'}</span>
          </button>
          {isTechnician && (
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/25 font-medium"
            >
              <Plus size={18} />
              Nueva Solicitud
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      <div className="space-y-6">
      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Estado</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="in-progress">En progreso</option>
              <option value="completed">Completada</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Prioridad</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="">Todas las prioridades</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
          </div>

          {!isTechnician && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Asignado a</label>
            <select
              value={filterAssignedTo}
              onChange={(e) => setFilterAssignedTo(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="">Todos</option>
              {assignableUsers.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Búsqueda</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Buscar..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="border-b border-r border-slate-200 px-4 py-3">Título</th>
                <th className="border-b border-r border-slate-200 px-4 py-3">Descripción</th>
                {!isTechnician && (
                  <th className="border-b border-r border-slate-200 px-4 py-3">Solicitado por</th>
                )}
                <th className="border-b border-r border-slate-200 px-4 py-3">Asignado a</th>
                <th className="border-b border-r border-slate-200 px-4 py-3">Prioridad</th>
                <th className="border-b border-r border-slate-200 px-4 py-3">Estado</th>
                <th className="border-b border-r border-slate-200 px-4 py-3">Fecha límite</th>
                <th className="border-b border-slate-200 px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedTasks.map(task => (
                <tr key={task.id} className="hover:bg-slate-50/70">
                  <td className="max-w-xs border-r border-slate-200 px-4 py-3 font-semibold text-slate-900">
                    <span className="line-clamp-2">{task.title}</span>
                  </td>
                  <td className="max-w-md border-r border-slate-200 px-4 py-3 text-slate-600">
                    <span className="line-clamp-2">{task.description || '-'}</span>
                  </td>
                  {!isTechnician && (
                    <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3 text-slate-600">
                      {task.assignedByName || '-'}
                    </td>
                  )}
                  <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3 text-slate-600">
                    {task.assignedToName || '-'}
                  </td>
                  <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3">
                    {getPriorityBadge(task.priority)}
                  </td>
                  <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3">
                    {getStatusBadge(task.status)}
                  </td>
                  <td className="whitespace-nowrap border-r border-slate-200 px-4 py-3 text-slate-600">
                    {task.dueDate ? formatDateDisplay(task.dueDate) : '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {!isTechnician && task.status !== 'completed' && (
                        <>
                          {task.status === 'pending' && (
                            <button
                              onClick={() => handleStatusChange(task, 'in-progress')}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Iniciar solicitud"
                            >
                              <Clock size={16} />
                            </button>
                          )}
                          {task.status === 'in-progress' && (
                            <button
                              onClick={() => handleStatusChange(task, 'completed')}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Completar solicitud"
                            >
                              <CheckCircle size={16} />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => handleEdit(task)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredTasks.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {filteredTasks.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-slate-200" />
          <p className="text-lg font-medium text-slate-600 mb-1">{emptyTitle}</p>
          <p className="text-sm text-slate-500">{emptyDescription}</p>
        </div>
      )}

      </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in">
            <div className="p-6 flex justify-between items-center border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <h3 className="font-heading font-bold text-slate-800">
                {isEditMode ? 'Editar Solicitud' : 'Nueva Solicitud'}
              </h3>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: Solicitar revisión de pago"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                  placeholder="Describe la solicitud..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className={`grid gap-4 ${isTechnician ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {!isTechnician && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Asignar a</label>
                  <select
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  >
                    <option value="">-- Sin asignar --</option>
                    {assignableUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                )}

                {!isTechnician && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prioridad</label>
                    <select
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      <option value="low">Baja</option>
                      <option value="medium">Media</option>
                      <option value="high">Alta</option>
                    </select>
                  </div>
                )}
              </div>

              <div className={`grid gap-4 ${isTechnician ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {!isTechnician && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Límite</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    />
                  </div>
                )}

                {isEditMode && !isTechnician && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                    <select
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="in-progress">En progreso</option>
                      <option value="completed">Completada</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="px-6 py-2.5 text-slate-600 font-medium hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover shadow-lg shadow-primary/25 transition-all"
                >
                  {isEditMode ? 'Actualizar' : 'Crear Solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        confirmClassName={confirmState.confirmClassName}
        onCancel={closeConfirmDialog}
        onConfirm={runConfirmAction}
      />
    </div>
  );
};

export default Tasks;
